import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import { selectDataForCart } from 'shared/product_tabs/cart/selector';
import { doAddToCart, destroyLineItems } from 'shared/product_tabs/cart/thunks';

import InitialMessage from 'shared/product_tabs/cart/components/InitialMessage';
import Fabrication from 'shared/product_tabs/cart/components/Fabrication';
import Assembly from 'shared/product_tabs/cart/components/Assembly';
import Stencils from 'shared/product_tabs/cart/components/Stencils';
import Parts from 'shared/product_tabs/cart/components/Parts';
import Discount from 'shared/product_tabs/cart/components/Discount';
import Accessories from 'shared/product_tabs/cart/components/Accessories';
import { validate as assemblyValidate } from 'shared/product_tabs/tab_assembly/slice/validator';
import { validate as stencilsValidate } from 'shared/product_tabs/tab_stencils/slice/validator';
import { validate as partsValidate } from 'shared/product_tabs/tab_parts/bomSlice';
import { buildForms as buildAssemblyForm } from 'shared/product_tabs/tab_assembly/utils/builders';
import { buildForms as buildStencilsForm } from 'shared/product_tabs/tab_stencils/slice/builder';
import { buildForms as buildBomForms } from 'shared/product_tabs/tab_parts/utils/builders';
import { buildForms as buildAccessoriesForms } from 'shared/product_tabs/tab_accessories/slice/builders';

const Cart = (props) => {
  const {
    fabricationSelector,
    buildFabricationForm,
    fabricationCleanUp,
    fabricationValidate,
    projectValidate,
    projectNumber,
    projectReference,
    importTariffsNotice,
  } = props;

  const dispatch = useDispatch();
  const [total, setTotal] = useState(0);
  const [enabled, setEnabled] = useState(false);
  const {
    fabricationProps,
    fabricationToDestroy,
    fabricationDataForForms,
  } = useSelector(fabricationSelector);

  const {
    addingToCart,
    assembly,
    stencils,
    accessories,
    parts,
  } = useSelector(selectDataForCart);

  const {
    stencilDataForForms,
    stencilsProps,
    stencilsToDestroy,
  } = stencils;
  const {
    assemblyDataForForms,
    assemblyProps,
    assemblyToDestroy,
  } = assembly;
  const {
    accessoriesDataForForms,
    accessoriesProps,
    accessoriesToDestroy,
  } = accessories;
  const {
    partsDataForForms,
    partsProps,
    partsToDestroy,
  } = parts;

  const validate = () => {
    let validationResult = true;
    validationResult = fabricationValidate();
    validationResult = dispatch(assemblyValidate()) && validationResult;
    validationResult = dispatch(stencilsValidate()) && validationResult;
    validationResult = dispatch(partsValidate(validationResult)) && validationResult;

    // Validate project parameters only when these tabs are selected
    if (fabricationProps.selected || assemblyProps.selected || stencilsProps.selected) {
      validationResult = projectValidate() && validationResult;
    }

    return validationResult;
  };

  const onAddToCart = async () => {
    if (!validate()) return;

    const lineItemsToDestroy = [
      ...fabricationToDestroy,
      ...partsToDestroy,
      ...assemblyToDestroy,
      ...stencilsToDestroy,
      ...accessoriesToDestroy,
    ];

    if (lineItemsToDestroy && lineItemsToDestroy.length > 0) {
      await dispatch(destroyLineItems(lineItemsToDestroy)).unwrap();
    }

    const project_number = projectNumber();
    const project_reference = projectReference();

    const forms = [
      ...buildFabricationForm({ ...fabricationDataForForms, project_number, project_reference }),
      ...buildAssemblyForm({ ...assemblyDataForForms, project_number, project_reference }),
      ...buildStencilsForm({ ...stencilDataForForms, project_number, project_reference }),
      ...buildAccessoriesForms({ ...accessoriesDataForForms, project_number, project_reference }),
      ...buildBomForms({...partsDataForForms, project_number, project_reference}),
    ];

    try {
      await dispatch(doAddToCart(forms)).unwrap();
      // eslint-disable-next-line no-undef
      window.location.href = '/cart';
    } catch (e) {
      toast.error(Spree.translations.add_to_cart_error);
    }
  };

  useEffect(() => {
    const tabs = [fabricationProps, assemblyProps, partsProps, stencilsProps, accessoriesProps];
    const selectedTabs = tabs.filter((tab) => tab.selected);

    let newTotal = selectedTabs.reduce((acc, tab) => acc + tab.subTotal, 0);

    // If both fabrication and stencils are selected, apply a 20% discount to the stencils
    if (fabricationProps.selected && stencilsProps.selected) {
      newTotal -= parseFloat(stencilsProps.subTotal) * 0.2;
    }

    newTotal *= 1;

    setEnabled(selectedTabs.length > 0);
    setTotal(newTotal);
  }, [
    fabricationProps,
    assemblyProps,
    stencilsProps,
    accessoriesProps,
    partsProps,
  ]);

  return (
    <>
      <div className="product-tabs-cart px-0 py-3">
        <div className="selection-label">
          {Spree.translations.order_summary}
          :
        </div>
        <InitialMessage selected={enabled} {...fabricationProps} />
        <Fabrication {...fabricationProps} cleanUp={fabricationCleanUp} />
        <Assembly {...assemblyProps} />
        <Parts {...partsProps} />
        <Stencils {...stencilsProps} />
        <Accessories {...accessoriesProps} />
        <Discount fabrication={fabricationProps} stencil={stencilsProps} />
        <div className="prices-section container-fluid px-0">
          <hr />
          <div className="sub-total container px-4">
            <div className="row no-gutters">
              <div className="col-4 label">
                {Spree.translations.subtotal}
              </div>
              <div className="col-8 price-container">
                <span id="display_total_price" className="total-price">
                  ${total.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
          <hr />
        </div>
        <div className="cart-section">
          <button
            type="button"
            className="btn btn-primary w-100 px-4"
            onClick={onAddToCart}
            disabled={!enabled || addingToCart}>
            {addingToCart && <span className="spinner-border spinner-border-sm mr-2" aria-hidden="true" />}
            {addingToCart ? Spree.translations.adding_to_cart : Spree.translations.add_to_cart}
          </button>
        </div>
      </div>
      {importTariffsNotice && (
        <div className="import-tariffs-notice">
          Please note that any applicable import tariffs will be charged by the shipping company.
        </div>
      )}
    </>
  );
};

export default Cart;
